import { useEffect } from "react";
import { useLocation } from "react-router";
import { useExperience } from "./useExperience";

// Content is always visible by default, including static HTML and failed JS.
// Only incoming elements animate, once per route; no scroll-loop or layout writes.
export function usePageMotion() {
  const { pathname } = useLocation();
  const { reducedMotion, paused } = useExperience();
  useEffect(() => {
    if (
      reducedMotion ||
      paused ||
      matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const main = document.getElementById("main");
    if (!main) return;
    const seen = new WeakSet<Element>();
    const animations = new Set<Animation>();
    const selector =
      ".section-heading, .project, .feature-copy, .feature-view, .expertise-intro, .expertise-list article, .process-heading, .about-portrait, .about-copy, .build-preview-heading, .build-preview-feature, .build-preview-note, .build-reflection, .contact-cta-content, .case-decisions > section, .case-reflection, .focus-heading, .focus-note, .focus-current, .focus-toolkit";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const animation = entry.target.animate(
            [
              { opacity: 0.35, transform: "translateY(22px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: 650, easing: "cubic-bezier(.22,1,.36,1)" },
          );
          animations.add(animation);
          animation.onfinish = () => animations.delete(animation);
        });
      },
      { threshold: 0.08 },
    );
    const scan = () =>
      main.querySelectorAll(selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        if (element.getBoundingClientRect().top >= innerHeight * 0.92)
          observer.observe(element);
      });
    scan();
    const mutation = new MutationObserver(scan);
    mutation.observe(main, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutation.disconnect();
      animations.forEach((animation) => animation.cancel());
    };
  }, [pathname, reducedMotion, paused]);
}
